/**
 * Centralized HTTP client for the React dashboard.
 *
 * Keycloak: registered via setKeycloak() from routes.js.
 * Redux store: registered via setStore() from index.js.
 * External/third-party URLs: use api/externalClient.js — not this module.
 */
import axios from "axios";

import { auth } from "../constants/actionTypes";
import { getUnauthorizedReason, UNAUTHORIZED_REASON } from "../helpers/authErrors";
import { isCanceledRequest } from "../helpers/isCanceledRequest";
import { generateRequestId, isValidRequestId } from "../helpers/requestId";
import { recordRequest } from "../reducers/requestTracking";

let keycloakInstance = null;
let storeInstance = null;

export function setKeycloak(instance) {
  keycloakInstance = instance;
}

export function setStore(store) {
  storeInstance = store;
}

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true
});

function getResponseRequestId(response, config) {
  const metadataRequestId = config?.metadata?.requestId;
  if (metadataRequestId) {
    return metadataRequestId;
  }

  const headers = response?.headers;
  if (!headers) {
    return null;
  }

  return headers["x-request-id"] || headers["X-Request-Id"] || null;
}

function dispatchRequestTracking(responseOrError) {
  if (!storeInstance) {
    return;
  }

  const isAxiosError = Boolean(responseOrError?.isAxiosError);
  const config = responseOrError?.config;
  if (!config) {
    return;
  }

  const response = isAxiosError ? responseOrError.response : responseOrError;
  const metadata = config.metadata || {};
  const requestId = getResponseRequestId(response, config);
  const durationMs = metadata.startTime != null ? Date.now() - metadata.startTime : null;
  const status = response?.status ?? null;
  const hasTransportError = isAxiosError && !response;
  const errorMessage = hasTransportError
    ? responseOrError.message || "Request failed"
    : isAxiosError && status != null
    ? responseOrError.message || `HTTP ${status}`
    : null;

  storeInstance.dispatch(
    recordRequest({
      requestId,
      url: config.url || "",
      method: (config.method || "get").toUpperCase(),
      status,
      durationMs,
      timestamp: Date.now(),
      errorMessage
    })
  );
}

apiClient.interceptors.request.use(
  async config => {
    const requestId = generateRequestId();
    const nextConfig = { ...config };
    const headers = { ...(nextConfig.headers || {}) };

    if (isValidRequestId(requestId)) {
      headers["X-Request-Id"] = requestId;
    }

    nextConfig.metadata = {
      ...(nextConfig.metadata || {}),
      requestId,
      startTime: Date.now()
    };

    if (keycloakInstance) {
      try {
        await keycloakInstance.updateToken(60);
        if (keycloakInstance.token) {
          headers.Authorization = `Bearer ${keycloakInstance.token}`;
        }
      } catch (error) {
        console.error("Failed to update keycloak token", error);
      }
    }

    nextConfig.headers = headers;
    return nextConfig;
  },
  error => Promise.reject(error)
);

/**
 * Marks the page URL so that privateRoutes signs the member out of Keycloak and routes.js shows the notice.
 * Only for a token that cannot be used: a signed-in member without an account must not end up here (GROWTH-7362).
 */
function flagAuthenticationError() {
  const currentUrl = window.location.href;
  const urlParams = new URLSearchParams(window.location.search);
  if (!urlParams.get("isAuthenticationError")) {
    urlParams.set("isAuthenticationError", "true");
  }
  const newUrl = `${currentUrl.split("?")[0]}?${urlParams.toString()}`;
  window.history.replaceState({ path: newUrl }, "", newUrl);
}

/**
 * The token is valid but the backend has no member for it. Records that in the store, with the id of the request
 * the backend refused so support can find it in the logs; the member stays signed in.
 */
function markAccountUnavailable(error) {
  if (!storeInstance) {
    flagAuthenticationError();
    return;
  }

  storeInstance.dispatch({
    type: auth.ACCOUNT_UNAVAILABLE,
    payload: { requestId: getResponseRequestId(error.response, error.config) }
  });
}

/**
 * The backend refused the token itself. Gets a new token from Keycloak and sends the same request once more;
 * when that is not possible, or the answer is a 401 again, the member is signed out as before.
 * A 401 is answered before the controller runs, so nothing was changed and the replay cannot repeat a write.
 */
function replayWithFreshToken(error) {
  const { config } = error;

  if (!keycloakInstance || !config || config.metadata?.authRetried) {
    flagAuthenticationError();
    return Promise.reject(error);
  }

  return Promise.resolve()
    .then(() => keycloakInstance.updateToken(-1))
    .then(
      () => apiClient.request({ ...config, metadata: { ...config.metadata, authRetried: true } }),
      () => {
        flagAuthenticationError();
        return Promise.reject(error);
      }
    );
}

function handleUnauthorized(error) {
  const requestUrl = error?.response?.config?.url || error?.config?.url || "";

  if (error.response?.status !== 401 || requestUrl.includes("/login")) {
    return Promise.reject(error);
  }

  const reason = getUnauthorizedReason(error);

  if (reason === UNAUTHORIZED_REASON.NO_MEMBER) {
    markAccountUnavailable(error);
    return Promise.reject(error);
  }

  if (reason === UNAUTHORIZED_REASON.TOKEN) {
    return replayWithFreshToken(error);
  }

  flagAuthenticationError();
  return Promise.reject(error);
}

apiClient.interceptors.response.use(
  response => {
    dispatchRequestTracking(response);
    return response;
  },
  error => {
    dispatchRequestTracking(error);
    return handleUnauthorized(error);
  }
);

/**
 * Sends a request that outlives the page (tab close, reload, leaving the site) with `fetch` keepalive.
 * api-v2 reads the member's token only from a header, which `navigator.sendBeacon` cannot send.
 *
 * Nothing after `pagehide` is guaranteed to run, so the request starts synchronously: the current
 * Keycloak token is used as it is (no refresh — a caller that can see the exit coming calls
 * `refreshToken()` earlier) and the axios interceptors (request tracking, 401 handling) are skipped.
 * Browsers cap keepalive bodies at 64 KB in total — keep them small.
 *
 * @param {string} method
 * @param {string} url API path, e.g. "/members/api-v2/user-following/communities"
 * @param {Object} data sent as JSON
 * @returns {Promise<Response>}
 */
export function sendKeepalive(method, url, data) {
  const headers = { "Content-Type": "application/json" };
  const requestId = generateRequestId();

  if (isValidRequestId(requestId)) {
    headers["X-Request-Id"] = requestId;
  }
  if (keycloakInstance?.token) {
    headers.Authorization = `Bearer ${keycloakInstance.token}`;
  }

  return fetch(`${process.env.REACT_APP_API_URL || ""}${url}`, {
    method,
    headers,
    body: JSON.stringify(data),
    credentials: "include",
    keepalive: true
  });
}

/**
 * Refreshes the Keycloak token in the background when it expires within a minute, so that a later
 * `sendKeepalive` — which cannot wait for a refresh — still carries a valid token. Never rejects.
 *
 * @returns {Promise<boolean>} whether the token was refreshed; false with no instance or on failure
 */
export function refreshToken() {
  if (!keycloakInstance) {
    return Promise.resolve(false);
  }

  return Promise.resolve()
    .then(() => keycloakInstance.updateToken(60))
    .then(Boolean)
    .catch(() => false);
}

// axios.create() instances do not inherit static helpers from the axios namespace.
apiClient.all = axios.all;
apiClient.spread = axios.spread;
apiClient.isCancel = isCanceledRequest;

export default apiClient;
